import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useAppSelector } from "@/store/hook";
import { Wrapper } from "@/layout/style";

// Lazy load Navbar and Footer - they're not critical for LCP
const Navbar = dynamic(() => import("@/layout/navbar"), {
  ssr: false,
});
const Footer = dynamic(() => import("@/layout/footer"), {
  ssr: false,
});

interface IProps {
  children: React.ReactNode | React.ReactElement;
}

const Layout = ({ children }: IProps) => {
  const router = useRouter();
  const modals = useAppSelector((state) => state.modals);

  // Memoize modal state calculation
  const open = useMemo(() => {
    return (
      modals.menu?.open ||
      modals.branchGallery?.open ||
      modals.eventDetails?.open ||
      modals.leadFormDrawer?.open ||
      modals.eventVideos?.open ||
      modals.video?.open ||
      modals.resultView?.open
    );
  }, [modals]);

  const { pathname, query } = router;

  // Memoize route checks
  const routeInfo = useMemo(() => {
    const isLead = [
      "/l",
      "/Registration",
      "/alumni",
      "/success-alumni",
      "/monthly-report",
      "/mr",
      "/d/sd",
      "/d/sf",
    ].some((path) => pathname.includes(path));
    const isHr = pathname.includes("hr") && query?.source;
    const isThankYou = pathname === "/thank-you";
    const showFooter = ![
      "/l",
      "/p/",
      "/Registration",
      "/thank-you",
      "/alumni",
      "/success-alumni",
      "/monthly-report",
      "/mr",
      "/d/sd",
      "/d/sf",
    ].some((path) => pathname.startsWith(path));
    const isNav = (!isThankYou && !isLead) || !!isHr;
    const isHome = pathname === "/";

    return { isNav, isHome, showFooter };
  }, [pathname, query?.source]);

  const { isNav, isHome, showFooter } = routeInfo;

  // Use CSS transitions instead of framer-motion for better performance
  const mainStyle: React.CSSProperties = useMemo(
    () => ({
      overflow: "auto",
      paddingTop: isNav && !isHome ? "68px" : "0",
      opacity: open ? 0 : 1,
      transition: "opacity 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)",
    }),
    [isNav, isHome, open]
  );

  return (
    <Wrapper padding={isNav ? !isHome : false} id="layout">
      {isNav && <Navbar />}
      <main style={mainStyle}>{children}</main>
      {showFooter && <Footer />}
    </Wrapper>
  );
};

export default Layout;
